import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { startTrainingSession } from '../../services/trainingService';
import { getBrand, BrandConfig } from '../../config/brands';
import '../../styles/training.css';

function generateMath(): { a: number; b: number } {
  return { a: Math.floor(Math.random() * 9) + 1, b: Math.floor(Math.random() * 9) + 1 };
}

export default function TrainingGatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const comp = searchParams.get('comp') || '';
  const brand: BrandConfig = getBrand(comp || undefined);

  const [math] = useState(generateMath);
  const [form, setForm] = useState({
    fullName: '', employeeRef: '', email: '', department: '', mathAnswer: '', _hp: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem('training_token');
    if (token) navigate(`/training/session/${token}`, { replace: true });
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.fullName.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!form.employeeRef.trim()) {
      setError('Please enter your employee reference.');
      return;
    }
    if (!form.email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (!form.mathAnswer.trim()) {
      setError('Please answer the verification question.');
      return;
    }

    setLoading(true);
    try {
      const result = await startTrainingSession({
        fullName: form.fullName,
        employeeRef: form.employeeRef,
        email: form.email,
        branchId: 1,        // ignored — comes from pre-registered record
        department: form.department,
        _hp: form._hp,
        mathAnswer: form.mathAnswer,
        mathExpected: math.a + math.b,
      });

      sessionStorage.setItem('training_token', result.sessionToken);
      sessionStorage.setItem('training_name', result.employeeName);
      sessionStorage.setItem('training_brand', brand.name);
      navigate(`/training/session/${result.sessionToken}`);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tg-gate">
      <div className="tg-gate__card">
        <div className="tg-gate__logo">{brand.name}</div>
        <h1 className="tg-gate__title">AML Training Programme</h1>
        <p className="tg-gate__subtitle">
          8-week programme under Regulation 24 of the Money Laundering Regulations 2017.
          Your responses will be stored and reviewed by your manager.
        </p>

        <form onSubmit={handleSubmit} className="tg-gate__form" autoComplete="off">
          {/* Honeypot */}
          <input name="_hp" value={form._hp} onChange={handleChange}
            tabIndex={-1} aria-hidden="true" className="tg-gate__hp" />

          <div className="tg-gate__row">
            <label className="tg-gate__label">Full Name *</label>
            <input
              className="tg-gate__input"
              name="fullName"
              value={form.fullName}
              onChange={handleChange}
              placeholder="Your name"
              autoComplete="off"
            />
          </div>

          <div className="tg-gate__row">
            <label className="tg-gate__label">Employee Reference *</label>
            <input
              className="tg-gate__input"
              name="employeeRef"
              value={form.employeeRef}
              onChange={handleChange}
              placeholder="Your employee reference (e.g. EMP-001)"
              autoComplete="off"
              autoFocus
            />
          </div>

          <div className="tg-gate__row">
            <label className="tg-gate__label">Email *</label>
            <input
              className="tg-gate__input"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Work email"
            />
          </div>

          <div className="tg-gate__row">
            <label className="tg-gate__label">Department</label>
            <input
              className="tg-gate__input"
              name="department"
              value={form.department}
              onChange={handleChange}
              placeholder="e.g. Compliance, Operations, Risk (optional)"
            />
          </div>

          <div className="tg-gate__row tg-gate__row--math">
            <label className="tg-gate__label">
              Verification: What is {math.a} + {math.b}? *
            </label>
            <input
              className="tg-gate__input tg-gate__input--short"
              name="mathAnswer"
              value={form.mathAnswer}
              onChange={handleChange}
              placeholder="Answer"
              autoComplete="off"
            />
          </div>

          {error && <p className="tg-gate__error">{error}</p>}

          <button className="tg-gate__btn" type="submit" disabled={loading}>
            {loading ? 'Checking…' : 'Begin Training'}
          </button>
        </form>

        <p className="tg-gate__note">
          Your employee reference is assigned by your manager. If you do not have one,
          please contact your line manager or compliance team.
        </p>
      </div>
    </div>
  );
}
